import { useQuery } from "@tanstack/react-query";
import { hash } from "ohash";
import { useEffect, useRef } from "react";
import { APP_RUNTIME, APP_SHELL, APP_VERSION } from "../../utils/appVersion";
import { analyticsQueue } from "../../utils/analyticsQueue";
import { ANALYTICS_PING_INTERVAL } from "../../utils/constants";
import { playerActivationStore } from "../../utils/firebase";
import { inactiveTimerMeasurer } from "../../utils/inactiveTimerMeasurer";
import { useVideoPlayerStore } from "../../utils/store";
import type { IMedia } from "../analytics/IAnalytics";
import { useAnalytics } from "../hooks/useAnalytics";
import { useVideosPreload } from "../hooks/useVideosPreload";
import { FullScreenPlayer } from "./FullScreenPlayer";
import { VideoErrorBar } from "./VideoErrorBar";

const STARTED_AT = Date.now();
const ANALYTICS_TIMEOUT_MS = 5000; // Timeout de 5 segundos para analytics

// Tipo dos dados de ping para a fila.
// Heartbeat: player, uptime, state and versions go out every cycle. The media
// list is only attached when it changes (see medias_included), so a ping is a
// few hundred bytes instead of the whole catalogue.
type PingData = {
	playerId: string;
	uptime: number;
	state: "tocando" | "screensaver" | "parado";
	medias_total: number;
	medias_count: number;
	medias_signature: string;
	medias_included: boolean;
	medias?: IMedia[];
	app_version: string;
	app_shell: string;
	app_runtime: string | null;
	timestamp: string;
};

// Função para processar fila de analytics pendentes
const processAnalyticsQueue = async (
	pingFn: (data: PingData) => Promise<boolean>,
) => {
	while (analyticsQueue.size > 0) {
		const pending = analyticsQueue.getQueue()[0];
		if (!pending) break;

		try {
			await Promise.race([
				pingFn(pending.data as PingData),
				new Promise((_, reject) =>
					setTimeout(() => reject(new Error("timeout")), ANALYTICS_TIMEOUT_MS),
				),
			]);
			// Sucesso - remove da fila
			analyticsQueue.dequeue();
		} catch {
			// Falha - para de tentar (tentará no próximo ciclo)
			break;
		}
	}
};

export const VideoAndTimeoutWrapper = ({
	children,
}: { children: React.ReactNode }) => {
	const videoPlayer = useVideoPlayerStore();
	const videosPreload = useVideosPreload();
	const analytics = useAnalytics();
	// Signature of the media list of the last ping, so the list is only uploaded
	// when it actually changes.
	const lastMediasSignature = useRef<string | null>(null);

	useQuery({
		queryKey: ["analytics"],
		queryFn: async () => {
			const password = playerActivationStore.activationPassword;
			if (!password) return null;

			let state: "tocando" | "screensaver" | "parado" = "parado";

			if (videoPlayer.isPlaying) {
				state = videoPlayer.isSplash ? "screensaver" : "tocando";
			}

			const medias: IMedia[] = videosPreload.loadedVideos.map((video) => ({
				id: video.id,
				name: video.title,
			}));
			const mediasSignature = hash(medias);
			const mediasIncluded = mediasSignature !== lastMediasSignature.current;
			lastMediasSignature.current = mediasSignature;

			const pingData: PingData = {
				playerId: password,
				uptime: Date.now() - STARTED_AT,
				state,
				medias_total: videosPreload.total,
				medias_count: medias.length,
				medias_signature: mediasSignature,
				medias_included: mediasIncluded,
				// Absent means "unchanged", not "empty".
				...(mediasIncluded ? { medias } : {}),
				// Lets the panel see which build each device runs, without logging in.
				app_version: APP_VERSION,
				app_shell: APP_SHELL,
				app_runtime: APP_RUNTIME,
				timestamp: new Date().toISOString(),
			};

			// Primeiro, tenta enviar fila pendente
			await processAnalyticsQueue(analytics.ping.bind(analytics));

			try {
				// Timeout de 5 segundos
				const result = await Promise.race([
					analytics.ping(pingData),
					new Promise<null>((_, reject) =>
						setTimeout(
							() => reject(new Error("Analytics timeout")),
							ANALYTICS_TIMEOUT_MS,
						),
					),
				]);
				return result;
			} catch (error) {
				// Falha - salva na fila para enviar depois
				analyticsQueue.enqueue({ playerId: password, data: pingData });
				console.warn("Analytics salvo na fila para envio posterior");
				return null;
			}
		},
		refetchInterval: ANALYTICS_PING_INTERVAL,
		cacheTime: 0,
		staleTime: 0,
		enabled: videosPreload.isFetching || videosPreload.isFetched,
		retry: false,
	});

	inactiveTimerMeasurer.setOnInactive(() => {
		if (videoPlayer.isPlaying) {
			// Is playing something, do nothing
			return;
		}

		const isHome = window.location.pathname === "/player";

		if (!isHome) {
			return;
		}

		// Play only if is in home
		videoPlayer.playSplash();
	});

	inactiveTimerMeasurer.setOnActive(() => {
		if (!videoPlayer.isPlaying) {
			return;
		}

		if (videoPlayer.isSplash) {
			videoPlayer.stop();
		}
	});

	useEffect(() => {
		const handleKeyDown = () => {
			inactiveTimerMeasurer.active();
		};
		const handleMouseMove = () => {
			inactiveTimerMeasurer.active();
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	return (
		<>
			<FullScreenPlayer />
			{children}
			<VideoErrorBar />
			{videosPreload.isFetching && (
				<div className="fixed bottom-0 right-0 left-0 bg-gray-900 text-white text-center p-2">
					{videosPreload.total === 0 ? (
						<CountingBar />
					) : (
						<ProgressBar
							total={videosPreload.total}
							loaded={videosPreload.loaded}
						/>
					)}
				</div>
			)}
		</>
	);
};

function ProgressBar(props: { total: number; loaded: number }) {
	return (
		<div
			className="bg-condor-red text-white p-2 rounded-xl whitespace-nowrap transition-all duration-100"
			style={{ width: `${(props.loaded / props.total) * 100}%` }}
		>
			Preparando vídeos... {props.loaded}/{props.total}
		</div>
	);
}

function CountingBar() {
	return (
		<div className="bg-black/50 text-white p-2 rounded-xl whitespace-nowrap transition-all duration-100">
			Verificando novos vídeos...
		</div>
	);
}
